import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const DocumentVerification = ({ documents, onDocumentAction }) => {
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  const getDocumentStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'text-success bg-success/10';
      case 'rejected': return 'text-error bg-error/10';
      case 'pending': return 'text-warning bg-warning/10';
      default: return 'text-muted-foreground bg-muted/10';
    }
  };

  const getQualityColor = (quality) => {
    switch (quality) {
      case 'high': return 'text-success';
      case 'medium': return 'text-warning';
      case 'low': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleDocumentVerify = (docId, action) => {
    onDocumentAction(docId, action);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Document Verification</h2>
        <div className="flex items-center space-x-2">
          <Icon name="FileCheck" size={20} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {documents?.filter(doc => doc?.status === 'verified')?.length}/{documents?.length} Verified
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Document List */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground">Uploaded Documents</h3>
          <div className="space-y-3">
            {documents?.map((document) => (
              <div
                key={document?.id}
                className={`border rounded-lg p-4 cursor-pointer transition-all duration-150 hover:shadow-sm ${
                  selectedDocument?.id === document?.id 
                    ? 'border-primary bg-primary/5' :'border-border bg-card'
                }`}
                onClick={() => setSelectedDocument(document)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                      <Icon name="FileText" size={20} className="text-muted-foreground" />
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground">{document?.type}</h4>
                      <p className="text-sm text-muted-foreground">{document?.filename}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${getDocumentStatusColor(document?.status)}`}>
                    {document?.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Quality:</span>
                    <span className={`font-medium ${getQualityColor(document?.quality)}`}>
                      {document?.quality}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Size:</span>
                    <span className="text-foreground">{document?.size}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Uploaded:</span>
                    <span className="text-foreground">
                      {new Date(document.uploadedAt)?.toLocaleDateString('en-GB')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Expiry:</span>
                    <span className="text-foreground">
                      {document?.expiryDate ? new Date(document.expiryDate)?.toLocaleDateString('en-GB') : 'N/A'}
                    </span>
                  </div>
                </div>

                {document?.status === 'pending' && (
                  <div className="flex space-x-2 mt-3 pt-3 border-t border-border">
                    <Button
                      size="sm"
                      variant="success"
                      onClick={(e) => {
                        e?.stopPropagation();
                        handleDocumentVerify(document?.id, 'verify');
                      }}
                      iconName="Check"
                      iconPosition="left"
                      iconSize={14}
                    >
                      Verify
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={(e) => {
                        e?.stopPropagation();
                        handleDocumentVerify(document?.id, 'reject');
                      }}
                      iconName="X"
                      iconPosition="left"
                      iconSize={14}
                    >
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Document Preview */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-foreground">Document Preview</h3>
            {selectedDocument && (
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleZoomOut}
                  iconName="ZoomOut"
                  iconSize={16}
                />
                <span className="text-sm text-muted-foreground">{Math.round(zoomLevel * 100)}%</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleZoomIn}
                  iconName="ZoomIn"
                  iconSize={16}
                />
              </div>
            )}
          </div>

          <div className="border border-border rounded-lg bg-muted/20 min-h-96 flex items-center justify-center overflow-hidden">
            {selectedDocument ? (
              <div className="w-full h-full flex items-center justify-center p-4">
                <div 
                  className="transition-transform duration-200"
                  style={{ transform: `scale(${zoomLevel})` }}
                >
                  <Image
                    src={selectedDocument?.imageUrl}
                    alt={`${selectedDocument?.type} document showing ${selectedDocument?.description}`}
                    className="max-w-full max-h-full object-contain rounded-lg shadow-sm"
                  />
                </div>
              </div>
            ) : (
              <div className="text-center">
                <Icon name="FileImage" size={48} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Select a document to preview</p>
              </div>
            )}
          </div>

          {selectedDocument && (
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-foreground">Document Information</h4>
              <div className="grid grid-cols-1 gap-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Type:</span>
                  <span className="text-foreground">{selectedDocument?.type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Document Number:</span>
                  <span className="text-foreground font-mono">{selectedDocument?.documentNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Issue Date:</span>
                  <span className="text-foreground">
                    {new Date(selectedDocument.issueDate)?.toLocaleDateString('en-GB')}
                  </span>
                </div>
                {selectedDocument?.notes && (
                  <div className="pt-2 border-t border-border">
                    <span className="text-muted-foreground text-xs">Notes:</span>
                    <p className="text-foreground text-sm mt-1">{selectedDocument?.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentVerification;